import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LifeBuoy, Mail, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';

const STATUS_LABEL = {
    submitted: 'Received', awaiting_information: 'Awaiting your info', awaiting_client_approval: 'Awaiting your approval',
    under_review: 'Reviewing', approved: 'Reviewing', backlog: 'Reviewing', planned: 'Reviewing',
    in_progress: 'In progress', awaiting_client_validation: 'Confirm resolved?',
    completed: 'Resolved', closed: 'Resolved', declined: 'Declined',
};
const STAGE_TONE = {
    received: 'bg-sky-100 text-sky-800', reviewing: 'bg-violet-100 text-violet-800',
    in_progress: 'bg-blue-100 text-blue-800', resolved: 'bg-emerald-100 text-emerald-800',
};

function RequestLinkForm() {
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!/\S+@\S+\.\S+/.test(email)) return toast.error('Please enter a valid email address.');
        setSending(true);
        try {
            await api.post('/public/support/my-requests/request', { email: email.trim() });
            setSent(true);
        } catch (e2) { toast.error(formatApiError(e2?.response?.data?.detail) || 'Something went wrong'); }
        finally { setSending(false); }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="my-requests-form-page">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 max-w-md w-full text-center">
                <LifeBuoy className="w-8 h-8 mx-auto" style={{ color: '#0F5F6B' }} />
                <h1 className="mt-3 text-[17px] font-bold text-slate-900">See all your requests</h1>
                {sent ? (
                    <div className="mt-4" data-testid="my-requests-sent-confirmation">
                        <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500" />
                        <p className="mt-2 text-[13.5px] text-slate-600">If that email has any requests on file, we've sent a link to view them. Check your inbox.</p>
                    </div>
                ) : (
                    <>
                        <p className="mt-2 text-[13px] text-slate-500">Enter the email address you used when submitting a request, and we'll send you a link to see everything you've logged.</p>
                        <form onSubmit={submit} className="mt-4 flex gap-2">
                            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@example.com"
                                   className="flex-1 h-10 rounded-md border border-slate-300 px-3 text-[13.5px]" data-testid="my-requests-email-input" />
                            <button type="submit" disabled={sending} className="h-10 px-4 rounded-md text-white font-semibold text-[13px] disabled:opacity-60"
                                    style={{ background: '#0F5F6B' }} data-testid="my-requests-send-btn">
                                <Mail size={15} />
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}

function RequestsListView({ token }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/public/support/my-requests/${token}`);
            setData(r.data);
        } catch (e) { setErr(formatApiError(e?.response?.data?.detail) || 'This link has expired.'); }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    if (err) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 text-center" data-testid="my-requests-error">
                <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-md shadow-sm">
                    <div className="font-semibold text-slate-800">{err}</div>
                    <Link to="/support/my-requests" className="mt-3 inline-block text-[13px] font-semibold hover:underline" style={{ color: '#0F5F6B' }}>Request a new link</Link>
                </div>
            </div>
        );
    }
    if (!data) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading…</div>;

    return (
        <div className="min-h-screen bg-slate-50" data-testid="my-requests-list-page">
            <header className="text-white px-6 py-5" style={{ background: '#0F5F6B' }}>
                <div className="max-w-2xl mx-auto flex items-center gap-2">
                    <LifeBuoy className="w-5 h-5 text-white/80" />
                    <div>
                        <div className="text-[11px] uppercase tracking-wide text-white/60">{data.email}</div>
                        <div className="text-[16px] font-semibold">Your requests</div>
                    </div>
                </div>
            </header>
            <main className="max-w-2xl mx-auto px-6 py-8">
                {data.items.length === 0 ? (
                    <div className="text-center text-[13.5px] text-slate-500" data-testid="my-requests-empty">You haven't logged any requests yet.</div>
                ) : (
                    <div className="space-y-2">
                        {data.items.map(t => (
                            <Link key={t.public_token} to={`/support/${t.public_token}`}
                                  className="flex items-center justify-between bg-white rounded-lg border border-slate-200 p-4 hover:border-slate-300 transition"
                                  data-testid={`my-requests-row-${t.public_token}`}>
                                <div className="min-w-0">
                                    <div className="text-[13.5px] font-medium text-slate-800 truncate">{t.title}</div>
                                    <div className="text-[11.5px] text-slate-400">{t.ticket_number} · {t.platform} · {(t.created_at || '').slice(0, 10)}</div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${STAGE_TONE[t.client_stage] || 'bg-slate-100 text-slate-600'}`}>{STATUS_LABEL[t.status] || t.status}</span>
                                    <ArrowRight size={14} className="text-slate-300" />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

export default function MyRequests() {
    const { token } = useParams();
    return token ? <RequestsListView token={token} /> : <RequestLinkForm />;
}
